import { Vec3, vec, normalize, clamp } from './math';
import type { ObstacleField } from './obstacles';

export const SURFACES = ['clay', 'sandClay', 'mud', 'sand', 'grass', 'roots', 'water'] as const;
export type Surface = (typeof SURFACES)[number];

/** Grip multiplier per surface, applied on top of BikeConfig.grip. */
export const SURFACE_GRIP: Record<Surface, number> = {
  clay: 1.0, sandClay: 0.9, mud: 0.6, sand: 0.7, grass: 0.75, roots: 0.5, water: 0.4,
};

export interface TerrainSample { height: number; normal: Vec3; surface: Surface }

/** Vertical cylinder the bike can hit (a tree trunk). */
export interface Obstacle { x: number; z: number; radius: number }

export interface Terrain {
  sample(x: number, z: number): TerrainSample;
  /** Cylinders whose axis is within `radius` + their own radius of (x, z). Optional: open ground has none. */
  obstacles?(x: number, z: number, radius: number): readonly Obstacle[];
  /** Static obstacles (trees, rocks, bales, props) with a broadphase grid. Optional. */
  readonly field?: ObstacleField;
  /**
   * Heights of n points (xs[i], zs[i]) into out[i]. Optional: one call instead of n spares the physics step the boxed
   * doubles of n calls (the 240 Hz step must not allocate).
   */
  heightsInto?(xs: Float64Array, zs: Float64Array, n: number, out: Float64Array): void;
  /** `sample` written into `out` (no allocation; the physics step uses it when present). Optional. */
  sampleInto?(x: number, z: number, out: TerrainSample): void;
  /** Height only (cheaper than `sample` when the normal is not needed). Optional. */
  heightAt?(x: number, z: number): number;
  /** Depth (m, >= 0) riding has pressed into the ground at (x, z), already included in `sample` heights. Optional. */
  rutDepth?(x: number, z: number): number;
}

export const flatTerrain = (height = 0, surface: Surface = 'clay'): Terrain => ({
  sample: () => ({ height, normal: vec(0, 1, 0), surface }),
});

/** Square grid of (cells + 1)^2 vertices starting at (originX, originZ). */
const HX = new Float64Array(1), HZ = new Float64Array(1), HO = new Float64Array(1);

export class Heightfield implements Terrain {
  readonly heights: Float32Array;
  readonly surfaces: Uint8Array;

  constructor(
    readonly cells: number,
    readonly cellSize: number,
    readonly originX: number,
    readonly originZ: number,
  ) {
    if (!Number.isInteger(cells) || cells < 1) throw new Error(`heightfield needs at least 1 cell, got ${cells}`);
    const n = (cells + 1) * (cells + 1);
    this.heights = new Float32Array(n);
    this.surfaces = new Uint8Array(n);
  }

  private index(i: number, j: number): number {
    return j * (this.cells + 1) + i;
  }

  fill(fn: (x: number, z: number) => number): void {
    for (let j = 0; j <= this.cells; j++) {
      for (let i = 0; i <= this.cells; i++) {
        this.heights[this.index(i, j)] = fn(this.originX + i * this.cellSize, this.originZ + j * this.cellSize);
      }
    }
  }

  setSurface(i: number, j: number, surface: Surface): void {
    const id = SURFACES.indexOf(surface);
    if (id < 0) throw new Error(`unknown surface: ${surface}`);
    this.surfaces[this.index(i, j)] = id;
  }

  /** heightAt(xs[i], zs[i]) into out[i]: doubles stay in typed arrays, so the 240 Hz step allocates nothing. */
  heightIdx(xs: Float64Array, zs: Float64Array, i: number, out: Float64Array): void {
    const x = xs[i]!, z = zs[i]!;
    const fx = clamp((x - this.originX) / this.cellSize, 0, this.cells);
    const fz = clamp((z - this.originZ) / this.cellSize, 0, this.cells);
    const ci = Math.min(Math.floor(fx), this.cells - 1);
    const j = Math.min(Math.floor(fz), this.cells - 1);
    const tx = fx - ci;
    const tz = fz - j;
    const h = this.heights;
    const h00 = h[this.index(ci, j)]!;
    const h10 = h[this.index(ci + 1, j)]!;
    const h01 = h[this.index(ci, j + 1)]!;
    const h11 = h[this.index(ci + 1, j + 1)]!;
    out[i] = (h00 * (1 - tx) + h10 * tx) * (1 - tz) + (h01 * (1 - tx) + h11 * tx) * tz;
  }

  heightsInto(xs: Float64Array, zs: Float64Array, n: number, out: Float64Array): void {
    for (let i = 0; i < n; i++) this.heightIdx(xs, zs, i, out);
  }

  heightAt(x: number, z: number): number {
    HX[0] = x; HZ[0] = z;
    this.heightIdx(HX, HZ, 0, HO);
    return HO[0]!;
  }

  sample(x: number, z: number): TerrainSample {
    const e = this.cellSize;
    const normal = normalize(vec(
      this.heightAt(x - e, z) - this.heightAt(x + e, z),
      2 * e,
      this.heightAt(x, z - e) - this.heightAt(x, z + e),
    ));
    return { height: this.heightAt(x, z), normal, surface: this.surfaceAt(x, z) };
  }

  sampleInto(x: number, z: number, out: TerrainSample): void {
    const e = this.cellSize;
    const nx = this.heightAt(x - e, z) - this.heightAt(x + e, z), ny = 2 * e, nz = this.heightAt(x, z - e) - this.heightAt(x, z + e);
    const l = Math.hypot(nx, ny, nz);
    out.normal.x = nx / l; out.normal.y = ny / l; out.normal.z = nz / l;
    out.height = this.heightAt(x, z);
    out.surface = this.surfaceAt(x, z);
  }

  /** Surface of the nearest grid vertex. */
  surfaceAt(x: number, z: number): Surface {
    const i = clamp(Math.round((x - this.originX) / this.cellSize), 0, this.cells);
    const j = clamp(Math.round((z - this.originZ) / this.cellSize), 0, this.cells);
    return SURFACES[this.surfaces[this.index(i, j)]!]!;
  }
}
